/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} True if valid email
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate phone number format (basic validation)
 * @param {string} phone - Phone number to validate
 * @returns {boolean} True if valid phone
 */
export const isValidPhone = (phone) => {
  // Allow various phone formats: +1234567890, 123-456-7890, (123) 456-7890, etc.
  const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;
  return phone.length >= 7 && phoneRegex.test(phone);
};

/**
 * Validate required field
 * @param {string} value - Value to check
 * @returns {boolean} True if value is not empty
 */
export const isRequired = (value) => {
  return value !== null && value !== undefined && value.toString().trim() !== '';
};

/**
 * Validate minimum length
 * @param {string} value - Value to check
 * @param {number} minLength - Minimum length
 * @returns {boolean} True if value meets minimum length
 */
export const minLength = (value, minLength) => {
  return value && value.length >= minLength;
};

/**
 * Validate maximum length
 * @param {string} value - Value to check
 * @param {number} maxLength - Maximum length
 * @returns {boolean} True if value doesn't exceed maximum length
 */
export const maxLength = (value, maxLength) => {
  return !value || value.length <= maxLength;
};

/**
 * Validate booking form data
 * @param {Object} formData - Form data to validate
 * @returns {Object} Validation result with isValid flag and errors object
 */
export const validateBookingForm = (formData) => {
  const errors = {};

  if (!isRequired(formData.name)) {
    errors.name = 'Name is required';
  }

  if (!isRequired(formData.email)) {
    errors.email = 'Email is required';
  } else if (!isValidEmail(formData.email)) {
    errors.email = 'Invalid email format';
  }

  if (!isRequired(formData.phone)) {
    errors.phone = 'Phone number is required';
  } else if (!isValidPhone(formData.phone)) {
    errors.phone = 'Invalid phone number format';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

/**
 * Validate signup form data
 * @param {Object} formData - Form data to validate
 * @returns {Object} Validation result with isValid flag and errors object
 */
export const validateSignupForm = (formData) => {
  const errors = {};

  if (!isRequired(formData.firstName)) {
    errors.firstName = 'First name is required';
  }

  if (!isRequired(formData.lastName)) {
    errors.lastName = 'Last name is required';
  }

  if (!isRequired(formData.email)) {
    errors.email = 'Email is required';
  } else if (!isValidEmail(formData.email)) {
    errors.email = 'Invalid email format';
  }

  if (!isRequired(formData.password)) {
    errors.password = 'Password is required';
  } else if (!minLength(formData.password, 6)) {
    errors.password = 'Password must be at least 6 characters';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};
